import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { ScanQueueItem } from '@/hooks/useScanQueue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  LabelTemplate, 
  LabelElement,
  FieldData, 
  LabelSheetConfig, 
  LABEL_SHEET_PRESETS,
  ShapeElement,
  ImageElement,
  IconElement,
  mmToPx,
  ptToMm,
  SCREEN_DPI,
  createSingleLabelConfig,
} from '@/types/label';
import { 
  Printer, 
  Upload, 
  Trash2, 
  Plus,
  Minus,
  ZoomIn,
  ZoomOut,
  Download,
  ExternalLink,
  Undo2,
  Redo2,
  Smartphone,
  Check,
  X,
  FileType,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { DataCarousel } from './DataCarousel';
import { generateBarcodeDataUrl, generateQRCodeDataUrl } from '@/lib/barcodes';
import { exportLabelPdf, exportMultiLabelPdf, generateLabelPdfBlob, generateMultiLabelPdfBlob } from '@/lib/pdfExport';
import { PdfPreviewModal } from './PdfPreviewModal';
import { FieldWithLookup } from './FieldWithLookup';
import { useScanQueue } from '@/hooks/useScanQueue';
import { toast } from 'sonner';

interface PrintPanelProps {
  template: LabelTemplate;
  fields: string[];
  fieldData: FieldData;
  onUpdateField: (field: string, value: string) => void;
  resolveContent: (content: string) => string;
  // Field undo/redo
  onFieldUndo?: () => void;
  onFieldRedo?: () => void;
  canUndoField?: boolean;
  canRedoField?: boolean;
  // CSV
  csvData: FieldData[];
  csvHeaders: string[];
  currentRowIndex: number;
  totalRows: number;
  onImportCsv: (file: File) => void;
  onClearCsv: () => void;
  onNextRow: () => void;
  onPrevRow: () => void;
  onGoToRow: (index: number) => void;
  // Text import (key:value)
  onImportText?: (file: File, opts: { mode: 'single' | 'multi'; separator?: string }) => Promise<unknown> | void;
  // Template fields for sample download
  templateFields?: string[];
  // Print
  onPrintSingle: () => void;
  onPrintBatch: (copies: number, sheetConfig: LabelSheetConfig) => void;
  // Mobile
  isMobile?: boolean;
  // Link URL pattern from preset
  presetLinkUrl?: string | null;
  // Field config
  onUpdateFieldConfig?: (field: string, key: string, value: any) => void;
}

export const PrintPanel: React.FC<PrintPanelProps> = ({
  template,
  fields,
  fieldData,
  onUpdateField,
  resolveContent,
  onFieldUndo,
  onFieldRedo,
  canUndoField = false,
  canRedoField = false,
  csvData,
  csvHeaders,
  currentRowIndex,
  totalRows,
  onImportCsv,
  onClearCsv,
  onNextRow,
  onPrevRow,
  onGoToRow,
  onImportText,
  templateFields,
  onPrintSingle,
  onPrintBatch,
  isMobile = false,
  presetLinkUrl,
  onUpdateFieldConfig,
}) => {
  const [copies, setCopies] = useState(1);
  const [sheetConfig, setSheetConfig] = useState<LabelSheetConfig>(createSingleLabelConfig(template.width, template.height));
  
  // Sync Single Label config when template dimensions change
  useEffect(() => {
    setSheetConfig(prev => {
      if (prev.name === 'Single Label') {
        return createSingleLabelConfig(template.width, template.height);
      }
      // For multi-label sheets, update the label dimensions to match template
      return { ...prev, labelWidth: template.width, labelHeight: template.height };
    });
  }, [template.width, template.height]);
  const [previewZoom, setPreviewZoom] = useState(100);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const txtInputRef = useRef<HTMLInputElement>(null);
  const [textMode, setTextMode] = useState<'single' | 'multi'>('single');
  const [textSeparator, setTextSeparator] = useState<string>('');
  
  // PDF Preview Modal state
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('label.pdf');
  // Listen for barcode scans from mobile scanner
  const handleScanReceived = useCallback((item: ScanQueueItem) => {
    // Only process if it matches current template
    if (item.template_id === template.id && item.field_data) {
      Object.entries(item.field_data).forEach(([field, value]) => {
        if (fields.includes(field) && value) {
          onUpdateField(field, value);
        }
      });
    }
  }, [template.id, fields, onUpdateField]);

  const { pendingScans, dismissScan, applyAndDismiss } = useScanQueue({ onScanReceived: handleScanReceived });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImportCsv(file);
    }
    e.target.value = '';
  };

  const handleTextFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onImportText) {
      onImportText(file, { mode: textMode, separator: textSeparator });
    }
    e.target.value = '';
  };

  const downloadSampleTxt = () => {
    const fieldsToUse = (templateFields && templateFields.length > 0) ? templateFields : (fields.length > 0 ? fields : ['name', 'value']);
    const lines = [
      '# One field per line. Use "key: value" or "key = value".',
      '# Lines starting with # or // are ignored.',
      '# Separate multiple labels with a blank line.',
      '',
      ...fieldsToUse.map((f) => `${f}: `),
      '',
      ...fieldsToUse.map((f) => `${f}: `),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'label-template-data.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (totalRows > 0) {
      onPrintBatch(copies, sheetConfig);
    } else {
      onPrintSingle();
    }
  };

  const handlePreviewPdf = async () => {
    setIsGeneratingPdf(true);
    setPdfModalOpen(true);
    setPdfBlob(null);
    
    try {
      let blob: Blob;
      let filename: string;
      
      if (totalRows > 0) {
        const batchResolve = (content: string, rowData?: Record<string, string>) => {
          return content.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
            if (rowData && rowData[fieldName]) {
              return rowData[fieldName];
            }
            return fieldData[fieldName] || match;
          });
        };
        blob = await generateMultiLabelPdfBlob(template, batchResolve, csvData, sheetConfig);
        filename = `labels-batch-${Date.now()}.pdf`;
      } else {
        blob = await generateLabelPdfBlob(template, resolveContent);
        filename = `label-${Date.now()}.pdf`;
      }
      
      setPdfBlob(blob);
      setPdfFilename(filename);
    } catch (error) {
      console.error('Failed to generate PDF preview:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = () => {
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setPdfModalOpen(false);
    }
  };

  return (
    <div className={`h-full min-h-0 flex overflow-hidden ${isMobile ? 'flex-col' : ''}`}>
      {/* Left: Form */}
      <div className={`${isMobile ? 'flex-1 min-h-0' : 'w-72 lg:w-80 xl:w-96'} shrink-0 ${isMobile ? 'border-b' : 'border-r'} border-border/50 flex flex-col bg-card min-h-0`}>
        <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-border/50">
          <h2 className="text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2">
            <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
            Quick Print
          </h2>
          <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
            Fill in the fields and print directly
          </p>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            {/* Template Info */}
            <div className="p-2 sm:p-3 bg-muted/50 rounded-lg">
              <p className="text-[10px] sm:text-xs font-medium">{template.name}</p>
              <p className="text-[9px] sm:text-[10px] text-muted-foreground">
                {template.width} × {template.height} mm • {template.elements.length} elements
              </p>
            </div>

            {/* Fields */}
            {fields.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Label Data
                  </Label>
                  <div className="flex items-center gap-1">
                    {onFieldUndo && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={onFieldUndo}
                        disabled={!canUndoField}
                        title="Undo field change"
                      >
                        <Undo2 className="w-3 h-3" />
                      </Button>
                    )}
                    {onFieldRedo && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={onFieldRedo}
                        disabled={!canRedoField}
                        title="Redo field change"
                      >
                        <Redo2 className="w-3 h-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[10px] sm:text-xs text-muted-foreground hover:text-foreground gap-1"
                      onClick={() => fields.forEach(f => onUpdateField(f, ''))}
                    >
                      <Trash2 className="w-3 h-3" />
                      Clear
                    </Button>
                  </div>
                </div>
                {fields.map((field) => (
                  <FieldWithLookup
                    key={field}
                    field={field}
                    value={fieldData[field] || ''}
                    onChange={onUpdateField}
                    lastChars={(template.fieldConfig as any)?.[field]?.lastChars || 0}
                    onLastCharsChange={(f, val) => onUpdateFieldConfig?.(f, 'lastChars', val)}
                    prefixSources={(template.fieldConfig as any)?.[field]?.prefixSources || []}
                    onPrefixSourcesChange={(f, sources) => onUpdateFieldConfig?.(f, 'prefixSources', sources)}
                    onFillFromRecord={(record) => {
                      Object.entries(record).forEach(([k, v]) => {
                        if (fields.includes(k) && k !== field) {
                          onUpdateField(k, v);
                        }
                      });
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="py-3 sm:py-4 text-center text-[10px] sm:text-xs text-muted-foreground">
                No dynamic fields in this template
              </div>
            )}

            <Separator />

            {/* CSV Import */}
            <div className="space-y-2 sm:space-y-3">
              <Label className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Batch Data (Optional)
              </Label>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
              />

              <input
                ref={txtInputRef}
                type="file"
                accept=".txt,text/plain"
                onChange={handleTextFileChange}
                className="hidden"
              />

              {totalRows === 0 ? (
                <div className="space-y-1.5">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full gap-1.5 sm:gap-2 h-8 text-[10px] sm:text-xs"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    Import CSV for batch printing
                  </Button>
                  {onImportText && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-1.5 sm:gap-2 h-8 text-[10px] sm:text-xs"
                        onClick={() => txtInputRef.current?.click()}
                      >
                        <FileType className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        Import Text File (.txt)
                      </Button>
                      <div className="flex items-center gap-1.5">
                        <Button
                          type="button"
                          variant={textMode === 'single' ? 'default' : 'outline'}
                          size="sm"
                          className="flex-1 h-7 text-[10px]"
                          onClick={() => setTextMode('single')}
                        >
                          Single
                        </Button>
                        <Button
                          type="button"
                          variant={textMode === 'multi' ? 'default' : 'outline'}
                          size="sm"
                          className="flex-1 h-7 text-[10px]"
                          onClick={() => setTextMode('multi')}
                        >
                          Multiple
                        </Button>
                      </div>
                      {textMode === 'multi' && (
                        <Input
                          value={textSeparator}
                          onChange={(e) => setTextSeparator(e.target.value)}
                          placeholder="Separator (blank line by default)"
                          className="h-7 text-[10px]"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full gap-1.5 h-7 text-[10px] text-muted-foreground"
                        onClick={downloadSampleTxt}
                      >
                        <Download className="w-3 h-3" />
                        Download sample .txt
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex items-center justify-between">
                    <DataCarousel
                      currentIndex={currentRowIndex}
                      totalRows={totalRows}
                      onGoToRow={onGoToRow}
                      onNext={onNextRow}
                      onPrev={onPrevRow}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive"
                      onClick={onClearCsv}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-[9px] sm:text-[10px] text-muted-foreground">
                    {totalRows} rows loaded from CSV
                  </p>
                </div>
              )}
            </div>

            <Separator />

            {/* Print Options */}
            <div className="space-y-2 sm:space-y-3">
              <Label className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Print Options
              </Label>

              {totalRows === 0 && (
                <div className="flex items-center gap-2">
                  <Label className="text-[10px] sm:text-xs flex-1">Copies</Label>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7 touch-target"
                      onClick={() => setCopies(Math.max(1, copies - 1))}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={copies}
                      onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
                      className="h-7 w-12 sm:w-14 text-center text-[10px] sm:text-xs"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7 touch-target"
                      onClick={() => setCopies(Math.min(100, copies + 1))}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-1 sm:space-y-1.5">
                <Label className="text-[10px] sm:text-xs">Sheet Layout</Label>
                <Select 
                  value={sheetConfig.name} 
                  onValueChange={(name) => {
                    const preset = LABEL_SHEET_PRESETS.find(p => p.name === name);
                    if (preset) {
                      if (preset.name === 'Single Label') {
                        setSheetConfig(createSingleLabelConfig(template.width, template.height));
                      } else {
                        setSheetConfig({ ...preset, labelWidth: template.width, labelHeight: template.height });
                      }
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-[10px] sm:text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LABEL_SHEET_PRESETS.map((preset) => (
                      <SelectItem key={preset.name} value={preset.name} className="text-xs">
                        {preset.name} ({preset.columns}×{preset.rows})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="text-[9px] sm:text-[10px] text-muted-foreground p-1.5 sm:p-2 bg-muted/50 rounded">
                <p>{sheetConfig.columns * sheetConfig.rows} labels per page</p>
                <p>Page: {sheetConfig.pageWidth} × {sheetConfig.pageHeight} mm</p>
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Scan Queue Review */}
        {pendingScans.length > 0 && (
          <div className="p-3 border-t border-border/50 space-y-2">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Smartphone className="w-3.5 h-3.5" />
              <span>Mobile Scans ({pendingScans.length})</span>
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {pendingScans.map(scan => (
                <div key={scan.id} className="bg-accent/30 rounded-md p-2 text-xs space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{scan.template_name}</span>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        title="Apply to fields"
                        onClick={() => {
                          if (scan.field_data) {
                            Object.entries(scan.field_data).forEach(([field, value]) => {
                              if (fields.includes(field) && value) {
                                onUpdateField(field, value as string);
                              }
                            });
                          }
                          applyAndDismiss(scan.id);
                          toast.success('Fields updated from scan');
                        }}
                      >
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        title="Dismiss"
                        onClick={() => dismissScan(scan.id)}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-muted-foreground space-y-0.5">
                    {Object.entries(scan.field_data || {}).map(([k, v]) => (
                      v ? <div key={k}><span className="font-medium">{k}:</span> {String(v)}</div> : null
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Print & Download Buttons */}
        <div className="p-3 sm:p-4 border-t border-border/50 bg-muted/30 space-y-1.5 sm:space-y-2">
          <Button 
            className="w-full gap-1.5 sm:gap-2 h-9 sm:h-10 text-xs sm:text-sm" 
            onClick={handlePrint}
          >
            <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            {totalRows > 0 
              ? `Print ${totalRows} Labels` 
              : copies > 1 
                ? `Print ${copies} Copies` 
                : 'Print Label'
            }
          </Button>
          <Button 
            variant="outline"
            className="w-full gap-1.5 sm:gap-2 h-9 sm:h-10 text-xs sm:text-sm" 
            onClick={handlePreviewPdf}
          >
            <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Preview & Download PDF
          </Button>
          {(() => {
            if (!presetLinkUrl) return null;
            // Resolve {{field}} placeholders in the link URL
            const resolvedUrl = presetLinkUrl.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
              return fieldData[fieldName] || '';
            });
            // Check if all placeholders were filled (no empty values remain)
            const hasEmptyPlaceholders = presetLinkUrl.replace(/\{\{(\w+)\}\}/g, (_, fieldName) => {
              return fieldData[fieldName]?.trim() || '';
            }).includes('');
            const allFilled = !presetLinkUrl.match(/\{\{(\w+)\}\}/g)?.some(match => {
              const fieldName = match.slice(2, -2);
              return !fieldData[fieldName]?.trim();
            });
            if (!allFilled) return null;
            // Extract a label from the URL domain
            let linkLabel = 'Open Link';
            try {
              const urlObj = new URL(resolvedUrl);
              linkLabel = `Open in ${urlObj.hostname.split('.').slice(-2, -1)[0] || urlObj.hostname}`;
            } catch { /* keep default label */ }
            return (
              <Button
                variant="outline"
                className="w-full gap-1.5 sm:gap-2 h-9 sm:h-10 text-xs sm:text-sm"
                onClick={() => window.open(resolvedUrl, '_blank')}
              >
                <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {linkLabel}
              </Button>
            );
          })()}
          <p className="text-[8px] sm:text-[10px] text-muted-foreground text-center">
            Print opens browser dialog • PDF opens preview first
          </p>
        </div>
      </div>

      {/* Right: Preview - Hidden on mobile */}
      {!isMobile && (
        <div className="flex-1 flex flex-col bg-workspace min-h-0">
          {/* Zoom Controls */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 bg-card/50">
            <span className="text-xs text-muted-foreground">Preview</span>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setPreviewZoom(Math.max(25, previewZoom - 25))}
                disabled={previewZoom <= 25}
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </Button>
              <div className="flex items-center gap-2 w-40">
                <Slider
                  value={[previewZoom]}
                  onValueChange={([value]) => setPreviewZoom(value)}
                  min={25}
                  max={500}
                  step={25}
                  className="w-full"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setPreviewZoom(Math.min(500, previewZoom + 25))}
                disabled={previewZoom >= 500}
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground w-10 text-right">{previewZoom}%</span>
            </div>
          </div>
          
          {/* Preview Content */}
          <div className="flex-1 overflow-auto p-6 min-h-0">
            <PrintPreview 
              template={template} 
              resolveContent={resolveContent}
              sheetConfig={sheetConfig}
              copies={totalRows > 0 ? 1 : copies}
              batchData={totalRows > 0 ? csvData : undefined}
              zoom={previewZoom}
            />
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        open={pdfModalOpen}
        onOpenChange={setPdfModalOpen}
        pdfBlob={pdfBlob}
        onDownload={handleDownloadPdf}
        isGenerating={isGeneratingPdf}
        filename={pdfFilename}
      />
    </div>
  );
};

// Print Preview Component
interface PrintPreviewProps {
  template: LabelTemplate;
  resolveContent: (content: string, data?: FieldData) => string;
  sheetConfig: LabelSheetConfig;
  copies: number;
  batchData?: FieldData[];
  zoom: number;
}

const PrintPreview: React.FC<PrintPreviewProps> = ({
  template,
  resolveContent,
  sheetConfig,
  copies,
  batchData,
  zoom,
}) => {
  const labelsPerPage = sheetConfig.columns * sheetConfig.rows;
  const totalLabels = batchData ? batchData.length : copies;
  const totalPages = Math.ceil(totalLabels / labelsPerPage);

  // Calculate scale to fit the label properly
  const mmToPxRatio = 3.78; // approximate mm to px at 96dpi
  const labelScale = (zoom / 100) * 1.5; // Scale for the MiniLabel rendering

  return (
    <div className="space-y-6">
      <div className="text-xs text-muted-foreground text-center">
        Preview: {totalLabels} label{totalLabels !== 1 ? 's' : ''} on {totalPages} page{totalPages !== 1 ? 's' : ''}
      </div>
      
      {Array.from({ length: Math.min(totalPages, 3) }).map((_, pageIndex) => (
        <div
          key={pageIndex}
          className="bg-white rounded-lg shadow-lg border border-border/50 mx-auto"
          style={{
            width: sheetConfig.pageWidth * mmToPxRatio * (zoom / 100),
            minHeight: sheetConfig.pageHeight * mmToPxRatio * (zoom / 100),
            padding: `${sheetConfig.marginTop * mmToPxRatio * (zoom / 100)}px ${sheetConfig.marginLeft * mmToPxRatio * (zoom / 100)}px`,
          }}
        >
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${sheetConfig.columns}, ${sheetConfig.labelWidth * mmToPxRatio * (zoom / 100)}px)`,
              gap: `${sheetConfig.verticalGap * mmToPxRatio * (zoom / 100)}px ${sheetConfig.horizontalGap * mmToPxRatio * (zoom / 100)}px`,
            }}
          >
            {Array.from({ length: Math.min(labelsPerPage, totalLabels - pageIndex * labelsPerPage) }).map((_, labelIndex) => {
              const globalIndex = pageIndex * labelsPerPage + labelIndex;
              const data = batchData?.[globalIndex];
              
              return (
                <div
                  key={labelIndex}
                  className="border border-dashed border-border/30 bg-white overflow-hidden"
                  style={{
                    width: sheetConfig.labelWidth * mmToPxRatio * (zoom / 100),
                    height: sheetConfig.labelHeight * mmToPxRatio * (zoom / 100),
                  }}
                >
                  <div 
                    className="origin-top-left"
                    style={{ 
                      transform: `scale(${(sheetConfig.labelWidth * mmToPxRatio * (zoom / 100)) / (mmToPx(template.width, SCREEN_DPI) * labelScale)})`,
                    }}
                  >
                    <MiniLabel 
                      template={template} 
                      resolveContent={(content) => 
                        data 
                          ? content.replace(/\{\{(\w+)\}\}/g, (_, field) => data[field] || `{{${field}}}`)
                          : resolveContent(content)
                      }
                      scale={labelScale}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      
      {totalPages > 3 && (
        <p className="text-xs text-muted-foreground text-center">
          + {totalPages - 3} more page{totalPages - 3 !== 1 ? 's' : ''}...
        </p>
      )}
    </div>
  );
};

// Mini Label renderer for preview - matches editor rendering
const MiniLabel: React.FC<{
  template: LabelTemplate;
  resolveContent: (content: string) => string;
  scale?: number;
}> = ({ template, resolveContent, scale: externalScale }) => {
  const baseScale = 3; // Same as editor default
  const scale = externalScale || baseScale;
  const [barcodeImages, setBarcodeImages] = useState<Record<string, string>>({});

  // Generate barcode/QR images using the same utilities as the editor
  useEffect(() => {
    const generateImages = async () => {
      const images: Record<string, string> = {};
      
      for (const element of template.elements) {
        if (element.visible === false) continue;
        
        const content = element.content ? resolveContent(element.content) : '';
        
        if (element.type === 'barcode') {
          const key = `${element.id}-${content}-${element.barScale || 2}-${element.barcodeType || 'code128'}-${element.barHeightRatio || 0.8}-${element.quietZone ?? 2}-${element.foregroundColor || ''}-${element.backgroundColor || ''}-${element.textSize || 8}`;
          images[key] = await generateBarcodeDataUrl(
            content, 
            element.size.width * scale, 
            element.size.height * scale,
            element.includeText,
            element.barScale || 2,
            element.barcodeType || 'code128',
            element.barHeightRatio || 0.8,
            element.quietZone ?? 2,
            element.foregroundColor,
            element.backgroundColor,
            element.textSize,
          );
        } else if (element.type === 'qrcode') {
          const key = `${element.id}-${content}-${element.foregroundColor || ''}-${element.backgroundColor || ''}`;
          const size = Math.min(element.size.width, element.size.height);
          images[key] = await generateQRCodeDataUrl(content, size * 10, element.foregroundColor, element.backgroundColor);
        }
      }
      
      setBarcodeImages(images);
    };
    
    generateImages();
  }, [template.elements, resolveContent, scale]);

  const getIconComponent = (iconName: string) => {
    const pascalCase = iconName
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join('');
    return (LucideIcons as any)[pascalCase];
  };

  const renderElement = (element: LabelElement) => {
    if (element.visible === false) return null;
    
    const x = mmToPx(element.position.x, SCREEN_DPI) * scale;
    const y = mmToPx(element.position.y, SCREEN_DPI) * scale;
    const width = mmToPx(element.size.width, SCREEN_DPI) * scale;
    const height = mmToPx(element.size.height, SCREEN_DPI) * scale;
    const content = element.content ? resolveContent(element.content) : '';

    const rotation = element.rotation || 0;
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      left: x,
      top: y,
      width,
      height,
      boxSizing: 'border-box',
      transform: rotation ? `rotate(${rotation}deg)` : undefined,
      transformOrigin: 'center center',
    };

    if (element.type === 'text') {
      // Convert pt to mm, then mm to px for accurate screen rendering (matches editor)
      const fontSizePx = mmToPx(ptToMm(element.fontSize), SCREEN_DPI) * scale;
      
      return (
        <div
          key={element.id}
          style={{
            ...baseStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: element.textAlign === 'center' ? 'center' : 
                           element.textAlign === 'right' ? 'flex-end' : 'flex-start',
            fontSize: fontSizePx,
            fontFamily: element.fontFamily,
            fontWeight: element.fontWeight,
            color: element.color || '#000',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {content}
        </div>
      );
    }

    if (element.type === 'barcode') {
      const imageKey = `${element.id}-${content}-${element.barScale || 2}-${element.barcodeType || 'code128'}-${element.barHeightRatio || 0.8}-${element.quietZone ?? 2}-${element.foregroundColor || ''}-${element.backgroundColor || ''}-${element.textSize || 8}`;
      return (
        <div key={element.id} style={baseStyle}>
          {barcodeImages[imageKey] ? (
            <img 
              src={barcodeImages[imageKey]} 
              alt="Barcode"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              draggable={false}
            />
          ) : (
            <div className="w-full h-full bg-muted/30 flex items-center justify-center">
              <div className="w-3/4 h-1/2 flex gap-0.5">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="flex-1 bg-foreground/20" style={{ width: i % 2 === 0 ? 2 : 1 }} />
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (element.type === 'qrcode') {
      const imageKey = `${element.id}-${content}-${element.foregroundColor || ''}-${element.backgroundColor || ''}`;
      return (
        <div key={element.id} style={baseStyle}>
          {barcodeImages[imageKey] ? (
            <img 
              src={barcodeImages[imageKey]} 
              alt="QR Code"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              draggable={false}
            />
          ) : (
            <div className="w-full h-full bg-muted/30 flex items-center justify-center">
              <div className="w-3/4 h-3/4 border-2 border-dashed border-foreground/20" />
            </div>
          )}
        </div>
      );
    }

    if (element.type === 'image') {
      const imgElement = element as ImageElement;
      return (
        <div
          key={element.id}
          style={{
            ...baseStyle,
            overflow: 'hidden',
            borderRadius: imgElement.borderRadius || 0,
          }}
        >
          {imgElement.src ? (
            <img 
              src={imgElement.src} 
              alt="Image"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: imgElement.objectFit || 'contain',
              }}
              draggable={false}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-xs">
              No image
            </div>
          )}
        </div>
      );
    }

    if (element.type === 'shape') {
      const shapeElement = element as ShapeElement;
      return (
        <div key={element.id} style={baseStyle}>
          {shapeElement.shapeType === 'rectangle' && (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: shapeElement.fillColor || 'transparent',
                border: `${shapeElement.strokeWidth || 1}px solid ${shapeElement.strokeColor || '#000'}`,
                borderRadius: shapeElement.borderRadius || 0,
              }}
            />
          )}
          {shapeElement.shapeType === 'circle' && (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: shapeElement.fillColor || 'transparent',
                border: `${shapeElement.strokeWidth || 1}px solid ${shapeElement.strokeColor || '#000'}`,
                borderRadius: '50%',
              }}
            />
          )}
          {shapeElement.shapeType === 'line' && (
            <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
              <line
                x1="0"
                y1="0"
                x2="100%"
                y2="100%"
                stroke={shapeElement.strokeColor || '#000'}
                strokeWidth={shapeElement.strokeWidth || 1}
              />
            </svg>
          )}
          {shapeElement.shapeType === 'triangle' && (
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
              <polygon
                points="50,0 0,100 100,100"
                fill={shapeElement.fillColor || 'transparent'}
                stroke={shapeElement.strokeColor || '#000'}
                strokeWidth={shapeElement.strokeWidth || 1}
              />
            </svg>
          )}
        </div>
      );
    }

    if (element.type === 'icon') {
      const iconElement = element as IconElement;
      const IconComponent = getIconComponent(iconElement.iconName);
      return (
        <div
          key={element.id}
          style={{
            ...baseStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {IconComponent && (
            <IconComponent 
              style={{ 
                width: '100%', 
                height: '100%', 
                color: iconElement.color || '#000' 
              }} 
            />
          )}
        </div>
      );
    }

    return null;
  };

  const widthPx = mmToPx(template.width, SCREEN_DPI) * scale;
  const heightPx = mmToPx(template.height, SCREEN_DPI) * scale;

  return (
    <div
      className="relative bg-white"
      style={{
        width: widthPx,
        height: heightPx,
      }}
    >
      {template.elements.map(renderElement)}
    </div>
  );
};
